import { useState, useEffect, useCallback } from 'react';
import { User } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { SubscriptionHookResult, UserData, SubscriptionData } from '../types/subscription';

// Cache for subscription data
const subscriptionCache = new Map<string, { data: UserData; timestamp: number }>();
const CACHE_DURATION = 30000; // 30 seconds

export const useUserSubscription = (user: User | null): SubscriptionHookResult => {
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUserData = useCallback(async () => {
    if (!user) {
      setSubscription(null);
      setUserData(null);
      setLoading(false);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Check cache first
      const cached = subscriptionCache.get(user.uid);
      const now = Date.now();

      if (cached && (now - cached.timestamp) < CACHE_DURATION) {
        const cachedData = cached.data;
        setUserData(cachedData);
        setSubscription(cachedData.subscription || null);
        setLoading(false);
        return;
      }

      const db = getFirestore();
      const userDoc = await getDoc(doc(db, 'webUsers', user.uid));

      if (userDoc.exists()) {
        const data = userDoc.data() as UserData;

        // Cache the data
        subscriptionCache.set(user.uid, {
          data,
          timestamp: now
        });

        setUserData(data);
        setSubscription(data.subscription || null);
      } else {
        setUserData(null);
        setSubscription(null);
        setError('User data not found');
      }
    } catch (err) {
      console.error('Error fetching user subscription:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch subscription data');
      setSubscription(null);
      setUserData(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const refetch = useCallback(async () => {
    if (user) {
      // Clear cache for this user
      subscriptionCache.delete(user.uid);
      await fetchUserData();
    }
  }, [user, fetchUserData]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  // Computed values
  const isProActive = subscription?.plan === 'pro' && subscription?.status === 'active';
  const isTrialActive = subscription?.plan === 'trial' && subscription?.status === 'active';

  const daysRemaining = (() => {
    if (!subscription?.trialEndsAt) return 0;

    try {
      let trialEnd: Date;

      if (subscription.trialEndsAt && typeof subscription.trialEndsAt === 'object' && 'toDate' in subscription.trialEndsAt) {
        trialEnd = (subscription.trialEndsAt as any).toDate();
      } else if (typeof subscription.trialEndsAt === 'number') {
        trialEnd = new Date(subscription.trialEndsAt * 1000);
      } else {
        trialEnd = new Date(subscription.trialEndsAt as any);
      }

      const now = new Date();
      const diffTime = trialEnd.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return Math.max(0, diffDays);
    } catch (error) {
      console.error('Error calculating days remaining:', error);
      return 0;
    }
  })();

  return {
    subscription,
    userData,
    loading,
    error,
    refetch,
    isProActive,
    isTrialActive,
    daysRemaining
  };
};